/**
 * GA4 funnel analytics — readable event names with form, button, and page context.
 * Website stream: REACT_APP_GA4_MEASUREMENT_ID (hynt-18). GTM: GTM-K7ZMJFK7.
 */

import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import {
  buildAdsRequirementEventName,
  getAdsRequirementPageTitle,
  getRouteInfo,
} from "./routeAnalytics";

const GA4_MEASUREMENT_ID = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
const IS_DEV = process.env.NODE_ENV === "development";

export const MOBILE_APP_MEASUREMENT_ID = "G-9CSB1MGDL5";

export const REQUIREMENT_SOURCE = {
  WEBSITE: "website",
  ADS: "ads",
};

/** Readable GA4 event names (≤40 chars, snake_case) */
export const FUNNEL_EVENTS = {
  POST_REQUIREMENT_START: "post_requirement_start",
  POST_REQUIREMENT_STEP_1: "post_requirement_step_1",
  POST_REQUIREMENT_STEP_2: "post_requirement_step_2",
  POST_REQUIREMENT_STEP_3: "post_requirement_step_3",
  POST_REQUIREMENT_SUCCESS: "post_requirement_success",

  CREATE_BUSINESS_START: "create_business_start",
  CREATE_BUSINESS_STEP_1: "create_business_step_1",
  CREATE_BUSINESS_STEP_2: "create_business_step_2",
  CREATE_BUSINESS_STEP_3: "create_business_step_3",
  CREATE_BUSINESS_SUCCESS: "create_business_success",

  CONTACT_US_START: "contact_us_start",
  CONTACT_US_SUCCESS: "contact_us_success",

  CLAIM_ACCOUNT_START: "claim_account_start",
  CLAIM_ACCOUNT_SUCCESS: "claim_account_success",

  JOB_APPLICATION_START: "job_application_start",
  JOB_APPLICATION_SUCCESS: "job_application_success",

  LEGACY_REQUIREMENT_START: "legacy_requirement_start",
  LEGACY_REQUIREMENT_SUCCESS: "legacy_requirement_success",
};

export const FORM_NAMES = {
  CONTACT_US: "Contact Us",
  REQUIREMENT_ENQUIRY: "Post a Requirement",
  ADS_POST_REQUIREMENT: "Ads Post a Requirement",
  JOB_APPLICATION: "Job Application",
  CREATE_BUSINESS: "Create Business",
  CLAIM_ACCOUNT: "Claim Account",
  LEGACY_REQUIREMENT: "Legacy Requirement",
};

/** @deprecated Use FUNNEL_EVENTS — kept for imports that still reference lead types */
export const LEAD_TYPES = {
  CONTACT: "contact",
  REQUIREMENT: "requirement",
  JOB_APPLICATION: "job_application",
  CREATE_BUSINESS: "create_business",
  CLAIM_ACCOUNT: "claim_account",
  LEGACY_REQUIREMENT: "legacy_requirement",
};

export const GA4_KEY_EVENT_NAMES = [
  FUNNEL_EVENTS.POST_REQUIREMENT_SUCCESS,
  "vastu_post_requirement_success",
  "interior_post_requirement_success",
  "architecture_post_requirement_success",
  "automation_post_requirement_success",
  FUNNEL_EVENTS.CREATE_BUSINESS_SUCCESS,
  FUNNEL_EVENTS.CONTACT_US_SUCCESS,
  FUNNEL_EVENTS.CLAIM_ACCOUNT_SUCCESS,
  FUNNEL_EVENTS.JOB_APPLICATION_SUCCESS,
  "generate_lead",
];

const formSuccessLastFired = new Map();
const funnelStepLastFired = new Map();
const FORM_SUCCESS_DEDUPE_MS = 5000;
let eventCounter = 0;

function getPageUrl() {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

function baseParams(pathname = "", search = "", { pageName, categoryParam } = {}) {
  const route = getRouteInfo(pathname, search, { categoryParam });
  const resolvedPageName =
    pageName || (categoryParam ? getAdsRequirementPageTitle(categoryParam) : "") || route.route_title;

  return {
    page_url: getPageUrl(),
    page_path: route.page_path,
    page_name: resolvedPageName,
    route_name: route.route_name,
    route_title: resolvedPageName,
    ...(categoryParam ? { category_param: categoryParam } : {}),
  };
}

function pushToDataLayer(payload) {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  const eventPayload = {
    ...payload,
    event_id: ++eventCounter,
    ...(GA4_MEASUREMENT_ID ? { ga4_measurement_id: GA4_MEASUREMENT_ID } : {}),
  };

  window.dataLayer.push(eventPayload);

  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", eventPayload.event, eventPayload);
  }
}

function toGa4EventParams(params = {}) {
  const out = {
    page_location: params.page_url || getPageUrl(),
    page_path: params.page_path || "",
    page_title: params.page_name || params.route_title || "",
  };
  if (params.form_name) out.form_name = String(params.form_name).slice(0, 100);
  if (params.button_name) out.button_name = String(params.button_name).slice(0, 100);
  if (params.page_name) out.page_name = String(params.page_name).slice(0, 100);
  if (params.traffic_source) out.traffic_source = String(params.traffic_source).slice(0, 40);
  if (params.funnel_step != null) out.funnel_step = String(params.funnel_step);
  if (params.category) out.category = String(params.category).slice(0, 100);
  return out;
}

function sendGa4Event(eventName, params = {}, { isLead = false } = {}) {
  if (!eventName || typeof window === "undefined") return;

  const ga4Params = {
    ...toGa4EventParams(params),
    ...(isLead ? { value: 1, currency: "INR" } : {}),
  };

  if (analytics) {
    logEvent(analytics, eventName, ga4Params);
  } else if (typeof window.gtag === "function" && GA4_MEASUREMENT_ID) {
    window.gtag("event", eventName, { send_to: GA4_MEASUREMENT_ID, ...ga4Params });
  }

  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.debug("[analytics ga4]", eventName, ga4Params);
  }
}

function shouldSkipDedupe(key, map, ms = FORM_SUCCESS_DEDUPE_MS) {
  const last = map.get(key);
  const now = Date.now();
  if (last != null && now - last < ms) return true;
  map.set(key, now);
  return false;
}

function trackEvent(
  eventName,
  {
    formName,
    buttonName,
    trafficSource,
    funnelStep,
    pageName,
    categoryParam,
    extra,
    isLead = false,
    dedupeKey,
  } = {},
) {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = {
    ...baseParams(pathname, search, { pageName, categoryParam }),
    form_name: formName || "",
    button_name: buttonName || "",
    ...(trafficSource ? { traffic_source: trafficSource } : {}),
    ...(funnelStep != null ? { funnel_step: funnelStep } : {}),
    ...extra,
  };

  if (dedupeKey && shouldSkipDedupe(dedupeKey, funnelStepLastFired)) return;

  pushToDataLayer({ event: eventName, ...params });
  sendGa4Event(eventName, params, { isLead });

  if (isLead) {
    sendGa4Event("generate_lead", params, { isLead: true });
  }
}

export function getRequirementTrafficSource({ directCategoryRoute = false } = {}) {
  return directCategoryRoute ? REQUIREMENT_SOURCE.ADS : REQUIREMENT_SOURCE.WEBSITE;
}

/** Maps internal flow step → funnel step number (1–3) by website vs ads */
export function getRequirementFunnelStepNumber(internalStep, source = REQUIREMENT_SOURCE.WEBSITE) {
  const isAds = source === REQUIREMENT_SOURCE.ADS;
  switch (internalStep) {
    case "category":
      return isAds ? null : 1;
    case "questions":
      return isAds ? 1 : 2;
    case "review":
      return isAds ? 2 : 3;
    case "verify":
    case "accounts":
    case "userDetails":
      return 3;
    default:
      return null;
  }
}

function isAdsRequirementSource(source) {
  return source === REQUIREMENT_SOURCE.ADS;
}

function requirementFormName(source, categoryParam = "") {
  if (isAdsRequirementSource(source) && categoryParam) {
    return getAdsRequirementPageTitle(categoryParam);
  }
  return isAdsRequirementSource(source)
    ? FORM_NAMES.ADS_POST_REQUIREMENT
    : FORM_NAMES.REQUIREMENT_ENQUIRY;
}

function requirementStartEventName(source, categoryParam = "") {
  if (isAdsRequirementSource(source) && categoryParam) {
    return buildAdsRequirementEventName(categoryParam, "start");
  }
  return FUNNEL_EVENTS.POST_REQUIREMENT_START;
}

function requirementStepEventName(
  stepNumber,
  source = REQUIREMENT_SOURCE.WEBSITE,
  categoryParam = "",
) {
  if (isAdsRequirementSource(source) && categoryParam) {
    if (stepNumber === 1) return buildAdsRequirementEventName(categoryParam, "step_1");
    if (stepNumber === 2) return buildAdsRequirementEventName(categoryParam, "step_2");
    if (stepNumber === 3) return buildAdsRequirementEventName(categoryParam, "step_3");
    return null;
  }
  if (stepNumber === 1) return FUNNEL_EVENTS.POST_REQUIREMENT_STEP_1;
  if (stepNumber === 2) return FUNNEL_EVENTS.POST_REQUIREMENT_STEP_2;
  if (stepNumber === 3) return FUNNEL_EVENTS.POST_REQUIREMENT_STEP_3;
  return null;
}

function requirementSuccessEventName(source, categoryParam = "") {
  if (isAdsRequirementSource(source) && categoryParam) {
    return buildAdsRequirementEventName(categoryParam, "success");
  }
  return FUNNEL_EVENTS.POST_REQUIREMENT_SUCCESS;
}

function resolveRequirementPageContext({ source, categoryParam } = {}) {
  if (!isAdsRequirementSource(source) || !categoryParam) return {};
  const pageName = getAdsRequirementPageTitle(categoryParam);
  return { pageName, categoryParam };
}

export function trackPostRequirementStart({
  source = REQUIREMENT_SOURCE.WEBSITE,
  buttonName = "Open form",
  categoryParam = "",
} = {}) {
  const eventName = requirementStartEventName(source, categoryParam);
  trackEvent(eventName, {
    formName: requirementFormName(source, categoryParam),
    buttonName,
    trafficSource: source,
    dedupeKey: eventName,
    ...resolveRequirementPageContext({ source, categoryParam }),
  });
}

export function trackPostRequirementStep({
  step,
  source = REQUIREMENT_SOURCE.WEBSITE,
  buttonName = "",
  internalStep = "",
  categoryParam = "",
} = {}) {
  const eventName = requirementStepEventName(step, source, categoryParam);
  if (!eventName) return;

  trackEvent(eventName, {
    formName: requirementFormName(source, categoryParam),
    buttonName: buttonName || `Step ${step}`,
    trafficSource: source,
    funnelStep: step,
    dedupeKey: eventName,
    extra: internalStep ? { internal_step: internalStep } : {},
    ...resolveRequirementPageContext({ source, categoryParam }),
  });
}

export function trackPostRequirementSuccess({
  source = REQUIREMENT_SOURCE.WEBSITE,
  buttonName = "Post the requirement",
  category = "",
  categoryParam = "",
} = {}) {
  const eventName = requirementSuccessEventName(source, categoryParam);
  if (shouldSkipDedupe(eventName, formSuccessLastFired)) return;

  trackEvent(eventName, {
    formName: requirementFormName(source, categoryParam),
    buttonName,
    trafficSource: source,
    isLead: true,
    extra: category ? { category } : {},
    ...resolveRequirementPageContext({ source, categoryParam }),
  });
}

export function trackCreateBusinessStart({ buttonName = "Open form" } = {}) {
  trackEvent(FUNNEL_EVENTS.CREATE_BUSINESS_START, {
    formName: FORM_NAMES.CREATE_BUSINESS,
    buttonName,
    dedupeKey: "create_business_start",
  });
}

export function trackCreateBusinessStep({ step, buttonName = "" } = {}) {
  const events = {
    1: FUNNEL_EVENTS.CREATE_BUSINESS_STEP_1,
    2: FUNNEL_EVENTS.CREATE_BUSINESS_STEP_2,
    3: FUNNEL_EVENTS.CREATE_BUSINESS_STEP_3,
  };
  const eventName = events[step];
  if (!eventName) return;

  trackEvent(eventName, {
    formName: FORM_NAMES.CREATE_BUSINESS,
    buttonName: buttonName || `Step ${step}`,
    funnelStep: step,
    dedupeKey: `create_business_step_${step}`,
  });
}

export function trackCreateBusinessSuccess({ buttonName = "Continue" } = {}) {
  const key = "create_business_success";
  if (shouldSkipDedupe(key, formSuccessLastFired)) return;

  trackEvent(FUNNEL_EVENTS.CREATE_BUSINESS_SUCCESS, {
    formName: FORM_NAMES.CREATE_BUSINESS,
    buttonName,
    isLead: true,
  });
}

export function trackContactUsStart({ buttonName = "Open form" } = {}) {
  trackEvent(FUNNEL_EVENTS.CONTACT_US_START, {
    formName: FORM_NAMES.CONTACT_US,
    buttonName,
    dedupeKey: "contact_us_start",
  });
}

export function trackContactUsSuccess({ buttonName = "Send message" } = {}) {
  const key = "contact_us_success";
  if (shouldSkipDedupe(key, formSuccessLastFired)) return;

  trackEvent(FUNNEL_EVENTS.CONTACT_US_SUCCESS, {
    formName: FORM_NAMES.CONTACT_US,
    buttonName,
    isLead: true,
  });
}

export function trackClaimAccountStart({ buttonName = "Open form" } = {}) {
  trackEvent(FUNNEL_EVENTS.CLAIM_ACCOUNT_START, {
    formName: FORM_NAMES.CLAIM_ACCOUNT,
    buttonName,
    dedupeKey: "claim_account_start",
  });
}

export function trackClaimAccountSuccess({ buttonName = "Submit" } = {}) {
  const key = "claim_account_success";
  if (shouldSkipDedupe(key, formSuccessLastFired)) return;

  trackEvent(FUNNEL_EVENTS.CLAIM_ACCOUNT_SUCCESS, {
    formName: FORM_NAMES.CLAIM_ACCOUNT,
    buttonName,
    isLead: true,
  });
}

export function trackJobApplicationStart({ buttonName = "Apply" } = {}) {
  trackEvent(FUNNEL_EVENTS.JOB_APPLICATION_START, {
    formName: FORM_NAMES.JOB_APPLICATION,
    buttonName,
    dedupeKey: `job_application_start::${buttonName}`,
  });
}

export function trackJobApplicationSuccess({ buttonName = "Submit application" } = {}) {
  const key = "job_application_success";
  if (shouldSkipDedupe(key, formSuccessLastFired)) return;

  trackEvent(FUNNEL_EVENTS.JOB_APPLICATION_SUCCESS, {
    formName: FORM_NAMES.JOB_APPLICATION,
    buttonName,
    isLead: true,
  });
}

export function trackLegacyRequirementStart({ buttonName = "Open form" } = {}) {
  trackEvent(FUNNEL_EVENTS.LEGACY_REQUIREMENT_START, {
    formName: FORM_NAMES.LEGACY_REQUIREMENT,
    buttonName,
    dedupeKey: "legacy_requirement_start",
  });
}

export function trackLegacyRequirementSuccess({ buttonName = "Submit" } = {}) {
  const key = "legacy_requirement_success";
  if (shouldSkipDedupe(key, formSuccessLastFired)) return;

  trackEvent(FUNNEL_EVENTS.LEGACY_REQUIREMENT_SUCCESS, {
    formName: FORM_NAMES.LEGACY_REQUIREMENT,
    buttonName,
    isLead: true,
  });
}

/** SPA page view — standard GA4 name with page_name / page_url params */
export function trackPageView(pathname, search = "", { categoryParam } = {}) {
  const base = baseParams(pathname, search, { categoryParam });
  const params = {
    ...base,
    page_location: getPageUrl(),
    page_title: base.page_name || (typeof document !== "undefined" ? document.title : ""),
  };

  pushToDataLayer({ event: "page_view", ...params });
  sendGa4Event("page_view", params);
}

/** @deprecated Use funnel-specific trackers */
export function trackLeadSubmitClick({ buttonName, formName, leadType } = {}) {
  if (leadType === LEAD_TYPES.CONTACT) {
    trackContactUsStart({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.CREATE_BUSINESS) {
    trackCreateBusinessStep({ step: 1, buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.JOB_APPLICATION) {
    trackJobApplicationStart({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.CLAIM_ACCOUNT) {
    trackClaimAccountStart({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.LEGACY_REQUIREMENT) {
    trackLegacyRequirementStart({ buttonName });
  }
}

/** @deprecated Use funnel-specific success trackers */
export function trackLeadFormSuccess({ buttonName, formName, leadType, extra, dedupeKey } = {}) {
  if (leadType === LEAD_TYPES.CONTACT) {
    trackContactUsSuccess({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.CREATE_BUSINESS) {
    trackCreateBusinessSuccess({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.REQUIREMENT) {
    trackPostRequirementSuccess({
      buttonName,
      source: extra?.traffic_source || REQUIREMENT_SOURCE.WEBSITE,
      category: extra?.category,
    });
    return;
  }
  if (leadType === LEAD_TYPES.JOB_APPLICATION) {
    trackJobApplicationSuccess({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.CLAIM_ACCOUNT) {
    trackClaimAccountSuccess({ buttonName });
    return;
  }
  if (leadType === LEAD_TYPES.LEGACY_REQUIREMENT) {
    trackLegacyRequirementSuccess({ buttonName });
  }
}

export function trackCallButtonClick({ buttonName, formName } = {}) {
  trackEvent("call_button_click", {
    formName: formName || "",
    buttonName: buttonName || "Call",
  });
}

export function initCallClickTracking(root = document) {
  if (!root?.addEventListener) return () => {};

  const handler = (event) => {
    const target = event.target.closest?.("[data-analytics-call]");
    if (!target) return;

    trackCallButtonClick({
      buttonName: target.getAttribute("data-analytics-call") || target.textContent?.trim() || "Call",
      formName: target.getAttribute("data-form-name") || "",
    });
  };

  root.addEventListener("click", handler, true);
  return () => root.removeEventListener("click", handler, true);
}

/** Call + phone tracking only — no global button_click noise */
export function initSiteAnalytics() {
  return initCallClickTracking(document);
}
